import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Select } from '@/components/ui';
import { addOption, listOptions } from '@/data/client';
import type { OptionKind } from '@/data/types';
import { FormError } from './Modal';

const ADD = '__add__';

/* A shared pick-list with "+ Add new…" at the bottom. A new option is saved for
   the whole workspace and selected immediately; the server returns the existing
   option instead of a duplicate if someone already added it. */
export function OptionSelect({ kind, label, value, onChange, required }: {
  kind: OptionKind; label: string; value: string; onChange: (id: string) => void; required?: boolean;
}) {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ['options', kind], queryFn: () => listOptions(kind), staleTime: 5 * 60_000 });
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const add = useMutation({
    mutationFn: () => addOption(kind, draft),
    onSuccess: (opt) => {
      void qc.invalidateQueries({ queryKey: ['options', kind] });
      onChange(opt.id);
      setAdding(false);
      setDraft('');
    },
  });

  if (adding) {
    return (
      <div className="crm-form">
        <div className="crm-addopt">
          <Input label={`New ${label.toLowerCase()}`} autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (draft.trim()) add.mutate(); } if (e.key === 'Escape') setAdding(false); }} />
          <Button size="sm" onClick={() => add.mutate()} disabled={!draft.trim() || add.isPending}>Add</Button>
          <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
        </div>
        <FormError error={add.error} />
      </div>
    );
  }
  return (
    <Select
      label={label}
      required={required}
      placeholder="Choose…"
      options={[...data.map((o) => ({ value: o.id, label: o.label })), { value: ADD, label: '+ Add new…' }]}
      value={value}
      onChange={(e) => (e.target.value === ADD ? setAdding(true) : onChange(e.target.value))}
    />
  );
}
